import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import { Models } from 'appwrite';
import { MenuItem } from 'primeng/api';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { BadgeModule } from 'primeng/badge';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AvatarModule } from 'primeng/avatar';
import { GlobalProgressSpinnerService } from '@shared/services/global-progress-spinner.service';
import { CartService } from 'app/core/pages/cart/cart.service';
import { Subscription } from 'rxjs';
import { HeaderService } from './header.service';
import { UserType } from '@shared/models/user.model';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    TieredMenuModule,
    BadgeModule,
    AngularSvgIconModule,
    RouterLink,
    AvatarModule,
  ],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
  isOpen: boolean = false;
  public screenHeight = `${window.innerHeight}px`;

  profileMenuItems: MenuItem[] = [
    {
      label: 'My Profile',
      icon: 'profile.svg',
      routerLink: ['/home/profile'],
      routerLinkActiveOptions: { exact: true },
    },
    {
      label: 'Bookmarks',
      icon: 'bookmarks.svg',
      routerLink: ['/home/properties/bookmarked'],
      routerLinkActiveOptions: { exact: true },
    },
    {
      separator: true,
    },
    {
      label: 'Logout',
      icon: 'logout.svg',
      command: () => {
        this.logout();
      },
    },
  ];

  menuItems: MenuItem[] = [
    {
      separator: true,
      state: [
        UserType.ADMIN,
        UserType.PROPERTY_MANAGER,
        UserType.INVESTOR,
        UserType.ANONYMOUS,
      ],
    },
    {
      label: 'Properties',
      icon: 'home.svg',
      routerLink: ['/home/properties'],
      routerLinkActiveOptions: { exact: true },
      state: [
        UserType.ADMIN,
        UserType.PROPERTY_MANAGER,
        UserType.INVESTOR,
        UserType.ANONYMOUS,
      ],
    },
    {
      label: 'Wallet',
      icon: 'wallet.svg',
      routerLink: ['/home/wallet'],
      routerLinkActiveOptions: { exact: true },
      state: [UserType.INVESTOR, UserType.ANONYMOUS],
    },
    {
      label: 'Portfolio',
      icon: 'portfolio.svg',
      routerLink: ['/home/portfolio'],
      routerLinkActiveOptions: { exact: true },
      state: [UserType.INVESTOR, UserType.ANONYMOUS],
    },
    {
      label: 'Rewards',
      icon: 'star.svg',
      routerLink: ['/home/rewards'],
      routerLinkActiveOptions: { exact: true },
      state: [UserType.INVESTOR, UserType.ANONYMOUS],
    },
    {
      label: 'Cart',
      icon: 'cart.svg',
      routerLink: ['/home/cart'],
      routerLinkActiveOptions: { exact: true },
      badge: '0',
      state: [UserType.INVESTOR, UserType.ANONYMOUS],
    },
    {
      label: 'Property management',
      icon: 'presentation.svg',
      routerLink: ['/home/property-management'],
      routerLinkActiveOptions: { exact: true },
      state: [UserType.PROPERTY_MANAGER],
    },
    {
      separator: true,
      state: [
        UserType.ADMIN,
        UserType.PROPERTY_MANAGER,
        UserType.INVESTOR,
        UserType.ANONYMOUS,
      ],
    },
    {
      label: 'User name',
      hasSubmenu: true,
      items: this.profileMenuItems,
      state: [UserType.ADMIN, UserType.PROPERTY_MANAGER, UserType.INVESTOR],
    },
  ];

  setMenuItems: MenuItem[] = [];
  currentUserType: UserType | null = null;

  activeRoute: string = '';
  user: Models.User<Models.Preferences> | null = null;

  private cartSub: Subscription = Subscription.EMPTY;
  private toggleMenuSub: Subscription = Subscription.EMPTY;

  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _globalProgressSpinnerService = inject(
    GlobalProgressSpinnerService
  );
  private readonly _cartService = inject(CartService);
  private readonly _headerSerive = inject(HeaderService);

  @HostListener('window:resize')
  onResize() {
    this.screenHeight = `${window.innerHeight}px`;
  }

  async ngOnInit() {
    this._authService.userType$.subscribe((userType) => {
      if (this.currentUserType === userType) {
        return;
      }

      this.currentUserType = userType;
      this.setupMenu();
    });

    this.setupMenu();

    this.toggleMenuSub = this._headerSerive.toggleMenu$.subscribe((isOpen) => {
      this.isOpen = isOpen;
    });
  }

  onHomePage(): void {
    this._router.navigate(['/home']);
  }

  onMenuToggle(): void {
    this.isOpen = !this.isOpen;
    this._headerSerive.setToggleMenu(this.isOpen);
  }

  private parseMenuItems(): void {
    const userType = this._authService.userType;
    let tmpMenuItems: MenuItem[] = [...this.menuItems];

    tmpMenuItems = tmpMenuItems.filter((item) =>
      (item.state as string[])?.includes(userType!)
    );

    // Set user name
    if (userType !== UserType.ANONYMOUS) {
      this.user = this._authService.user;

      this.menuItems = [...this.menuItems];

      const initials = this.user?.name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase();

      this.menuItems[8].label = this.user?.name;
      this.menuItems[8].title = initials;

      this.cartSub = this._cartService.cart$.subscribe((cart) => {
        const cartItem = this.menuItems[5];
        cartItem.badge = cart.length.toString();
      });

      this._cartService.getCart();
    }

    this.setMenuItems = tmpMenuItems;
  }

  private setupMenu(): void {
    this.parseMenuItems();

    this.activeRoute = this._router.url;
    this.setMenuItemActive();

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = this._router.url;
        this.setMenuItemActive();
      }
    });
  }

  private setMenuItemActive(): void {
    setTimeout(() => {
      const elements = document.querySelectorAll('.menu-item-active-header');
      elements.forEach((el) => {
        // Set focus
        const element = el as HTMLElement;
        element.click();

        // Clean up previously added menu-item-content-active
        const previousActiveElements = document.querySelectorAll(
          '.menu-item-content-active-header'
        );
        previousActiveElements.forEach((el) => {
          el.classList.remove('menu-item-content-active-header');
        });

        // Set menu-item-content-active class
        const parentNode = element.parentNode as HTMLElement;
        parentNode.classList.add('menu-item-content-active-header');
      });
    });
  }

  async logout() {
    this._globalProgressSpinnerService.showLoader();

    try {
      await this._authService.logout();
      this._router.navigate(['/login']);
      this._globalProgressSpinnerService.hideLoader();
    } catch (error) {
      this._globalProgressSpinnerService.hideLoader();
      console.error('Logout failed:', error);
    }
  }

  ngOnDestroy(): void {
    this.cartSub.unsubscribe();
    this.toggleMenuSub.unsubscribe();
  }
}
